import React from "react";
import { CompanyCardStyled, RemoveButton } from "./styled";
import { CompanyHeaderMedia } from "../../../../../components/CompanyHeaderMedia";
import { navigate } from "@reach/router";
import { ACCOUNT_TYPES_GROUPING } from "../../../../../constants/profileTypes";
import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { useCompanyProfileTypes } from "newui/store/common-types";
import SkeletonBase from "newui/components/Skeleton";
import { Company } from "../../types";

interface CompanyCardProps {
  className?: string;
  company: Company;
  companyGrouping?: string;
  isAdminMode: boolean;
  isSearchShortlist?: boolean;
  linkNewTab?: boolean;
  handleRemoveButtonClick?: (id: string | number, type: string, extra: string) => void;
}

/**
 * Displays company in a card format
 * @param param0 
 * @returns 
 */
export const CompanyCard: React.FC<CompanyCardProps> = ({
  handleRemoveButtonClick,
  linkNewTab = false,
  className,
  companyGrouping,
  company,
  isAdminMode = false
}) => {
  const COLORREADYTOVIEW = "#00C59C";
  const COLORFOUNDINGMEMBER = "#4864F9";
  const COLORPROFILEUNDERCONSTRUCTION = "#F38744";
  const { isSuperAdmin } = useCurrentUser();

  const link = isAdminMode && isSuperAdmin
    ? `/account/${company?.id}/company-profile/company-information`
    : `/company/${company?.id}`;

  const currentCompanyGrouping = useCurrentCompany()?.grouping;
  const { isLoading: isProfileTypeLoading, values: profileTypes } = useCompanyProfileTypes();
  const profileType = profileTypes?.find((type) => type.value === company?.profile_type?.id);

  const bgColorBasedStatus = (status?: string) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
      case "new member":
        return COLORFOUNDINGMEMBER;
      case "profile ready to view":
        return COLORREADYTOVIEW;
      case "profile under construction":
        return COLORPROFILEUNDERCONSTRUCTION;
      default:
        return "none";
    }
  };

  const navigateFunc = (
    link: string,
    linkNewTab?: boolean,
    isProfileUnderConstruction?: boolean
  ) => {
    if (isProfileUnderConstruction) return;

    if (isSuperAdmin) {
      linkNewTab ? window.open(link, "_blank") : navigate(link);
      return;
    }

    if (
      (currentCompanyGrouping === ACCOUNT_TYPES_GROUPING.procurer &&
        companyGrouping &&
        currentCompanyGrouping === companyGrouping) ||
      (currentCompanyGrouping === ACCOUNT_TYPES_GROUPING.bidderAndProcurer &&
        companyGrouping === ACCOUNT_TYPES_GROUPING.procurer)
    ) {
      return;
    }

    if (
      currentCompanyGrouping === ACCOUNT_TYPES_GROUPING.procurer ||
      currentCompanyGrouping === ACCOUNT_TYPES_GROUPING.bidderAndProcurer
    ) {
      linkNewTab ? window.open(link, "_blank") : navigate(link);
    }
  };

  const generateClassName = (divClassName?: string) =>
    divClassName?.trim() ? divClassName.replace(/ /g, "-").toLowerCase() : "";

  const statuses = company.company_profile_status?.includes(" and ")
    ? company.company_profile_status.split(" and ").reverse()
    : [company.company_profile_status];

  const isProfileUnderConstruction =
    statuses?.filter((status) => status === "Profile under construction")?.length > 0;

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleRemoveButtonClick?.(company?.id, "shortlist", "");
  };

  const profileTypeDisplay = isProfileTypeLoading ? <SkeletonBase /> : <p className="company-card__subtitle px-1 capitalize">{profileType?.label}</p>;

  return (
    <CompanyCardStyled
      onClick={() => navigateFunc(link, linkNewTab, isProfileUnderConstruction)}
      id={`company company-${company?.id}`}
      data-testid="company-card"
      style={{ cursor: isProfileUnderConstruction ? "auto" : "pointer" }}
      className={className}
    >
      <div className="company-card__inner" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <div className="company-card__header" style={{ position: "relative" }}>
          <div
            className="status-container"
            style={{ position: "absolute", display: "flex", gap: "8px", margin: "16px 12px", zIndex: 99 }}
          >
            {statuses?.length > 0 &&
              statuses.map((status, index) => (
                <div
                  key={index}
                  style={{ position: "relative", width: "max-content" }}
                  className={generateClassName(status)}
                >
                  {status && (
                    <div
                      style={{
                        height: "36px",
                        background: bgColorBasedStatus(status),
                        textAlign: "center",
                        top: 0,
                        width: "100%",
                        zIndex: 10,
                        color: "#fff",
                        fontFamily: "roboto",
                        fontSize: "14px",
                        fontWeight: 400,
                        padding: "8px 8px",
                        borderRadius: "6px",
                      }}
                    >
                      {status.toUpperCase()}
                    </div>
                  )}
                </div>
              ))}
          </div>

          {handleRemoveButtonClick && !isSuperAdmin && (
            <RemoveButton type="remove" data-testid="button-remove" onClick={handleRemove}>
              remove
            </RemoveButton>
          )}

          <CompanyHeaderMedia
            bg={company.banner_path ?? `${process.env.PUBLIC_URL}/img/placeholder-lg.jpg`}
            logo={company.logo_path ?? `${process.env.PUBLIC_URL}/img/placeholder-lg.jpg`}
          />
        </div>

        <div
          style={{
            textAlign: "center",
            fontSize: "1.375rem",
            display: "block",
            fontWeight: 500,
            color: "rgb(38, 56, 89)",
            lineHeight: 1.25,
            margin: "0px 0px 0.75rem",
            padding: "0px 1rem",
          }}
        >
          {company.name}
        </div>

        {profileTypeDisplay}
        {company?.location?.value && <p className="company-card__subtitle px-1">{company?.location.value}</p>}
      </div>
    </CompanyCardStyled>
  );
};
